'use client'

/**
 * Plantilla imprimible de las guías de carta.
 *
 * Orden deliberado: **primero imprime, después ofrece**. Antes el botón de
 * imprimir no imprimía — abría un formulario de email — así que el visitante
 * pedía una cosa y recibía otra, justo en la única acción de alta intención de
 * la página. Ahora el click imprime, y la oferta aparece después, cuando ya
 * tiene lo que vino a buscar y el "no" no le cuesta nada.
 *
 * Qué se ofrece depende de si la llamada del personaje existe hoy
 * (`isLiveCallCharacter`, que particiona `GuideCharacterKey` en llamada viva y
 * temporada): la llamada, o el aviso por email de cuando abra la agenda. Nunca
 * las dos: pedir dos cosas a la vez es no pedir ninguna.
 *
 * La impresión usa reglas @media print que aíslan `.guide-print-area`.
 *
 * El copy llega resuelto en servidor como un único modelo (`print`): las tablas
 * de este bloque cubren los siete idiomas de guía, y tenerlas aquí las habría
 * metido en el bundle de cada guía. Es UN objeto, no locale + strings + oferta
 * por separado, porque props sueltas dejaban un estado imposible: guía de
 * `es` con `strings` de portugués (revisión de #450).
 */

import { forwardRef, useRef, useState } from 'react'
import Link from 'next/link'
import type { GuideCharacterKey, GuideLocale, GuideTemplate } from '@/lib/guides'
import { printElement } from '@/lib/guides/tools/print-element'
import { getGuideCharacterAuthHref } from '@/lib/guides/character-cta'
import { getLegalHref } from '@/lib/legal-links'
import type {
  PostPrintOffer,
  ResolvedPostPrintOffer,
  ResolvedTemplatePrint,
  TemplatePrintStrings,
} from '@/lib/guides/template-print-copy'
import { Button } from '@/components/ui/button'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

type Step = 'idle' | 'offer' | 'done'

const TemplateSheet = forwardRef<HTMLElement, { template: GuideTemplate }>(function TemplateSheet(
  { template },
  ref
) {
  return (
    <figure
      ref={ref}
      className="rounded-2xl bg-white/95 p-6 text-slate-800 shadow-xl md:p-8"
    >
      <figcaption className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {template.title}
      </figcaption>
      <div className="space-y-3 font-serif text-lg leading-relaxed">
        {template.lines.map((line) => (
          <p key={line.slice(0, 40)}>{line}</p>
        ))}
      </div>
    </figure>
  )
})

/** Oferta de llamada: un enlace al flujo, sin formulario que rellenar. */
function CallOffer({
  locale,
  character,
  offer,
  trialNote,
  onDismiss,
  dismissLabel,
  onNavigate,
}: {
  locale: GuideLocale
  character: GuideCharacterKey
  offer: PostPrintOffer
  /** Ya resuelta con el flag de trial: null si la prueba está apagada. */
  trialNote: string | null
  onDismiss: () => void
  dismissLabel: string
  onNavigate: () => void
}) {
  return (
    <div className="space-y-4 rounded-2xl border border-white/15 bg-white/5 p-5">
      <p className="text-lg font-semibold text-white">{offer.title}</p>
      <p className="max-w-xl text-base text-white/80">{offer.context}</p>
      <div className="flex flex-wrap items-center gap-4">
        <Button
          asChild
          className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
        >
          <Link
            href={getGuideCharacterAuthHref(character, locale)}
            onClick={onNavigate}
          >
            {offer.action}
          </Link>
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-white/60 underline underline-offset-4 hover:text-white"
        >
          {dismissLabel}
        </button>
      </div>
      {trialNote && <p className="text-sm text-white/60">{trialNote}</p>}
    </div>
  )
}

type WaitlistFormProps = {
  locale: GuideLocale
  slug: string
  offer: PostPrintOffer
  strings: TemplatePrintStrings
  submitting: boolean
  error: boolean
  email: string
  onEmailChange: (email: string) => void
  onSubmit: (event: React.FormEvent) => void
  onDismiss: () => void
}

/** Oferta de aviso por email, para los personajes de temporada. */
function WaitlistForm({
  locale,
  slug,
  offer,
  strings,
  submitting,
  error,
  email,
  onEmailChange,
  onSubmit,
  onDismiss,
}: WaitlistFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-white/15 bg-white/5 p-5">
      <p className="text-lg font-semibold text-white">{offer.title}</p>
      <p className="max-w-xl text-base text-white/80">{offer.context}</p>
      <div>
        <label htmlFor={`lead-email-${slug}`} className="mb-1 block text-sm text-white/80">
          {strings.emailLabel}
        </label>
        <input
          id={`lead-email-${slug}`}
          type="email"
          required
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder={strings.emailPlaceholder}
          className="w-full max-w-sm rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-white/60 focus:outline-none"
        />
      </div>
      <label className="flex max-w-xl items-start gap-3 text-sm text-white/80">
        <input type="checkbox" required className="mt-1" />
        <span>
          {strings.consent}{' '}
          <a href={getLegalHref(locale, 'privacy')} className="underline hover:text-white">
            {strings.privacy}
          </a>
        </span>
      </label>
      {error && <p className="text-sm text-red-300">{strings.error}</p>}
      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
        >
          {submitting ? strings.submitting : offer.action}
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-white/60 underline underline-offset-4 hover:text-white"
        >
          {strings.dismiss}
        </button>
      </div>
    </form>
  )
}

export function GuideTemplatePrint({
  print,
  slug,
  template,
}: {
  print: ResolvedTemplatePrint
  slug: string
  template: GuideTemplate
}) {
  // locale, personaje, microcopy y oferta posterior llegan casados del
  // servidor; aquí solo se descomponen para leerlos.
  const { locale, character, strings, postPrintOffer } = print
  const [step, setStep] = useState<Step>('idle')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)
  const sheetRef = useRef<HTMLElement>(null)
  const analyticsProperties = {
    character_id: character,
    guide_slug: slug,
    locale,
    offer: postPrintOffer.kind,
  }

  function handlePrint() {
    if (sheetRef.current) printElement(sheetRef.current)
    captureProductEvent(PRODUCT_EVENTS.GUIDE_TEMPLATE_PRINTED, analyticsProperties)
    setStep('offer')
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(false)
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale, source: `guide:${slug}` }),
      })
      if (!response.ok) throw new Error(`status ${response.status}`)
      captureProductEvent(PRODUCT_EVENTS.GUIDE_LEAD_SUBMITTED, analyticsProperties)
      setStep('done')
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <TemplateSheet ref={sheetRef} template={template} />
      <div className="mt-4 space-y-4 print:hidden">
        <Button
          type="button"
          onClick={handlePrint}
          className="rounded-full bg-white/15 px-6 py-5 text-base font-semibold text-white hover:bg-white/25"
        >
          🖨️ {step === 'idle' ? strings.printCta : strings.printedAgain}
        </Button>

        {step === 'offer' &&
          (postPrintOffer.kind === 'call' ? (
            <CallOffer
              locale={locale}
              character={character}
              offer={postPrintOffer.offer}
              trialNote={postPrintOffer.trialNote}
              onDismiss={() => setStep('idle')}
              dismissLabel={strings.dismiss}
              onNavigate={() =>
                captureProductEvent(PRODUCT_EVENTS.GUIDE_CTA_CLICKED, {
                  ...analyticsProperties,
                  placement: 'post_print_offer',
                })
              }
            />
          ) : (
            <WaitlistForm
              locale={locale}
              slug={slug}
              strings={strings}
              offer={postPrintOffer.offer}
              submitting={submitting}
              error={error}
              email={email}
              onEmailChange={setEmail}
              onSubmit={handleSubmit}
              onDismiss={() => setStep('idle')}
            />
          ))}

        {step === 'done' && <p className="text-base text-emerald-200">✓ {strings.done}</p>}
      </div>
    </div>
  )
}
